import { useAuth } from "hooks/auth";
import { querystring } from "models/Listing";
import useSWR from "swr";
import useSWRImmutable from "swr/immutable";
import { Nullable } from "types/global";
import { ListingProps } from "types/listing";
import { getEnvVariable } from "utilities/env";

export const useTranslations = () => {
  const { fetcher } = useAuth();

  const useTranslationsList = (props?: ListingProps) => {
    const link = `${getEnvVariable("REACT_APP_API_URL")}/language${querystring(props)}`;
    return useSWRImmutable(link, () => fetcher(link));
  };

  const useLanguageData = (languageCode: string) => {
    return useSWR(
      () =>
        languageCode
          ? `${getEnvVariable("REACT_APP_API_URL")}/language/${languageCode}`
          : null,
      fetcher,
    );
  };

  const addLanguage = async (values: {
    adminDefault?: boolean;
    apiDefault?: boolean;
    code: string;
    name: string;
    order: Nullable<number>;
  }) => {
    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/language`,
      {
        body: JSON.stringify(values),
        headers: { "Content-Type": "application/json" },
        method: "post",
      },
    );
    const data = await response.json();
    if (response.ok) {
      return { data };
    } else {
      throw data;
    }
  };

  const editLanguage = async (
    languageCode: string,
    values: {
      adminDefault?: boolean;
      apiDefault?: boolean;
      name?: string;
    },
  ) => {
    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/language/${languageCode}`,
      {
        body: JSON.stringify(values),
        headers: { "Content-Type": "application/json" },
        method: "put",
      },
    );
    const data = await response.json();
    if (response.ok) {
      return { data };
    } else {
      throw data;
    }
  };

  const updateTranslation = async (
    languageCode: string,
    translationKey: string,
    translationValue: string,
  ) => {
    const response = await fetcher(
      `${getEnvVariable(
        "REACT_APP_API_URL",
      )}/language/${languageCode}/translation/${translationKey}`,
      {
        body: JSON.stringify({ translation: { value: translationValue } }),
        headers: { "Content-Type": "application/json" },
        method: "put",
      },
    );
    const data = await response.json();
    if (response.ok) {
      return { data };
    } else {
      throw data;
    }
  };

  const useTranslationsKeys = () => {
    return useSWR(
      `${getEnvVariable("REACT_APP_API_URL")}/translation/key`,
      fetcher,
    );
  };

  const useDefaultTranslations = () => {
    return useSWR(
      `${getEnvVariable("REACT_APP_API_URL")}/translation`,
      fetcher,
    );
  };

  const removeTranslation = async (translationID: string) => {
    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/language/${translationID}`,
      { method: "delete" },
    );
    if (response.status === 204) {
      return true;
    } else {
      throw await response.json();
    }
  };

  return {
    addLanguage,
    editLanguage,
    removeTranslation,
    updateTranslation,
    useDefaultTranslations,
    useLanguageData,
    useTranslationsKeys,
    useTranslationsList,
  };
};
